import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CMSContentType,
  CMSFieldDefinition,
  CMSObject,
  CMSTemplate,
  CreateCMSPayload,
  UpdateCMSPayload,
  normalizeCMSObject,
} from '@/client/types/cms';
import { cmsKeys } from './cms.query';
import { menuKeys } from '@/client/api/menus/menus.query';

const CMS = '/api/admin/cms';
const TIMEOUT = { timeout: 30_000 };

const invalidatePage = (qc: ReturnType<typeof useQueryClient>, cmsoid?: number) => {
  qc.invalidateQueries({ queryKey: cmsKeys.lists() });
  qc.invalidateQueries({ queryKey: cmsKeys.all });
  if (cmsoid != null) {
    qc.invalidateQueries({ queryKey: cmsKeys.detail(cmsoid) });
    qc.invalidateQueries({ queryKey: cmsKeys.pageFields(cmsoid) });
    qc.invalidateQueries({ queryKey: cmsKeys.pageTags(cmsoid) });
    qc.invalidateQueries({ queryKey: cmsKeys.pageMenus(cmsoid) });
    qc.invalidateQueries({ queryKey: cmsKeys.history(cmsoid) });
    qc.invalidateQueries({ queryKey: cmsKeys.slugHistory(cmsoid) });
  }
};

const unwrapPage = (raw: any): CMSObject => normalizeCMSObject(raw?.data ?? raw);

export const useCreateCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateCMSPayload) => {
    const raw = await request<any>(CMS, 'POST', payload, TIMEOUT);
    return unwrapPage(raw);
  }, 'Failed to create page');
  return useMutation({
    mutationFn: (payload: CreateCMSPayload) => fn(payload),
    onSuccess: () => invalidatePage(qc),
  });
};

export const useUpdateCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: UpdateCMSPayload) => {
    const { cmsoid, ...body } = payload;
    const raw = await request<any>(`${CMS}/${cmsoid}`, 'PUT', body, TIMEOUT);
    return unwrapPage(raw);
  }, 'Failed to update page');
  return useMutation({
    mutationFn: (payload: UpdateCMSPayload) => fn(payload),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const usePublishCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cmsoid: number) => {
    const raw = await request<any>(`${CMS}/${cmsoid}/publish`, 'POST', {}, TIMEOUT);
    return unwrapPage(raw);
  }, 'Failed to publish page');
  return useMutation({
    mutationFn: (cmsoid: number) => fn(cmsoid),
    onSuccess: (_d, cmsoid) => invalidatePage(qc, cmsoid),
  });
};

export const useUnpublishCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cmsoid: number) => {
    const raw = await request<any>(`${CMS}/${cmsoid}/unpublish`, 'POST', {}, TIMEOUT);
    return unwrapPage(raw);
  }, 'Failed to unpublish page');
  return useMutation({
    mutationFn: (cmsoid: number) => fn(cmsoid),
    onSuccess: (_d, cmsoid) => invalidatePage(qc, cmsoid),
  });
};

export const useDeleteCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; hard?: boolean }) => {
    return request<any>(`${CMS}/${args.cmsoid}?hard_delete=${!!args.hard}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete page');
  return useMutation({
    mutationFn: (args: { cmsoid: number; hard?: boolean }) => fn(args),
    onSuccess: () => invalidatePage(qc),
  });
};

export const useDuplicateCMSMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cmsoid: number) => {
    const raw = await request<any>(`${CMS}/${cmsoid}/duplicate`, 'POST', {}, TIMEOUT);
    return unwrapPage(raw);
  }, 'Failed to duplicate page');
  return useMutation({
    mutationFn: (cmsoid: number) => fn(cmsoid),
    onSuccess: () => invalidatePage(qc),
  });
};

export const useRestoreCMSRevisionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; cmshid: number }) => {
    const raw = await request<any>(
      `${CMS}/${args.cmsoid}/history/${args.cmshid}/restore`,
      'POST',
      {},
      TIMEOUT,
    );
    return unwrapPage(raw);
  }, 'Failed to restore revision');
  return useMutation({
    mutationFn: (args: { cmsoid: number; cmshid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useDeleteCMSSlugHistoryMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; cshid: number }) => {
    return request<any>(
      `${CMS}/${args.cmsoid}/slug-history/${args.cshid}`,
      'DELETE',
      {},
      TIMEOUT,
    );
  }, 'Failed to release slug redirect');
  return useMutation({
    mutationFn: (args: { cmsoid: number; cshid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => {
      qc.invalidateQueries({ queryKey: cmsKeys.slugHistory(cmsoid) });
    },
  });
};

export const useSetCMSPageFieldsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cmsoid: number; values: { cfdid: number; value: unknown }[] }) => {
      return request<any>(`${CMS}/${args.cmsoid}/fields`, 'PUT', { values: args.values }, TIMEOUT);
    },
    'Failed to save page fields',
  );
  return useMutation({
    mutationFn: (args: { cmsoid: number; values: { cfdid: number; value: unknown }[] }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useSetCMSPageTagsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; tagids: number[] }) => {
    return request<any>(`${CMS}/${args.cmsoid}/tags`, 'PUT', { tagids: args.tagids }, TIMEOUT);
  }, 'Failed to save page tags');
  return useMutation({
    mutationFn: (args: { cmsoid: number; tagids: number[] }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useClearCMSPageFieldMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; cfdid: number }) => {
    return request<any>(`${CMS}/${args.cmsoid}/fields/${args.cfdid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to clear field value');
  return useMutation({
    mutationFn: (args: { cmsoid: number; cfdid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useLinkCMSPageMenuMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; menid: number }) => {
    return request<any>(`${CMS}/${args.cmsoid}/menus/${args.menid}`, 'POST', {}, TIMEOUT);
  }, 'Failed to link menu');
  return useMutation({
    mutationFn: (args: { cmsoid: number; menid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => {
      invalidatePage(qc, cmsoid);
      qc.invalidateQueries({ queryKey: menuKeys.all });
    },
  });
};

export const useUnlinkCMSPageMenuMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; menid: number }) => {
    return request<any>(`${CMS}/${args.cmsoid}/menus/${args.menid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to unlink menu');
  return useMutation({
    mutationFn: (args: { cmsoid: number; menid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => {
      invalidatePage(qc, cmsoid);
      qc.invalidateQueries({ queryKey: menuKeys.all });
    },
  });
};

export const useReorderCMSPagesMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (order: { cmsoid: number; sort_order: number }[]) => {
      return request<any>(`${CMS}/reorder`, 'PUT', { order }, TIMEOUT);
    },
    'Failed to reorder pages',
  );
  return useMutation({
    mutationFn: (order: { cmsoid: number; sort_order: number }[]) => fn(order),
    onSuccess: () => invalidatePage(qc),
  });
};

export const useDetachPageAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; asid: number }) => {
    return request<any>(`${CMS}/${args.cmsoid}/assets/${args.asid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to detach asset');
  return useMutation({
    mutationFn: (args: { cmsoid: number; asid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useAttachPageAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { cmsoid: number; asid: number; featured?: boolean }) => {
      return request<any>(
        `${CMS}/${args.cmsoid}/assets/${args.asid}`,
        'POST',
        { featured: args.featured ?? true },
        TIMEOUT,
      );
    },
    'Failed to attach asset',
  );
  return useMutation({
    mutationFn: (args: { cmsoid: number; asid: number; featured?: boolean }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useSetFeaturedAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cmsoid: number; asid: number }) => {
    return request<any>(`${CMS}/${args.cmsoid}/assets/featured/${args.asid}`, 'PUT', {}, TIMEOUT);
  }, 'Failed to set featured asset');
  return useMutation({
    mutationFn: (args: { cmsoid: number; asid: number }) => fn(args),
    onSuccess: (_d, { cmsoid }) => invalidatePage(qc, cmsoid),
  });
};

export const useClearFeaturedAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cmsoid: number) => {
    return request<any>(`${CMS}/${cmsoid}/assets/featured`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to clear featured asset');
  return useMutation({
    mutationFn: (cmsoid: number) => fn(cmsoid),
    onSuccess: (_d, cmsoid) => invalidatePage(qc, cmsoid),
  });
};

export const useCreateContentTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (body: Partial<CMSContentType>) => {
    return request<any>(`${CMS}/content-types`, 'POST', body, TIMEOUT);
  }, 'Failed to create content type');
  return useMutation({
    mutationFn: (body: Partial<CMSContentType>) => fn(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'content-types'] }),
  });
};

export const useUpdateContentTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cctid: number } & Partial<CMSContentType>) => {
    const { cctid, ...body } = args;
    return request<any>(`${CMS}/content-types/${cctid}`, 'PATCH', body, TIMEOUT);
  }, 'Failed to update content type');
  return useMutation({
    mutationFn: (args: { cctid: number } & Partial<CMSContentType>) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'content-types'] }),
  });
};

export const useDeleteContentTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cctid: number) => {
    return request<any>(`${CMS}/content-types/${cctid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete content type');
  return useMutation({
    mutationFn: (cctid: number) => fn(cctid),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'content-types'] }),
  });
};

export const useCreateTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (body: Partial<CMSTemplate>) => {
    return request<any>(`${CMS}/templates`, 'POST', body, TIMEOUT);
  }, 'Failed to create template');
  return useMutation({
    mutationFn: (body: Partial<CMSTemplate>) => fn(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'templates'] }),
  });
};

export const useUpdateTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { ctplid: number } & Partial<CMSTemplate>) => {
    const { ctplid, ...body } = args;
    return request<any>(`${CMS}/templates/${ctplid}`, 'PATCH', body, TIMEOUT);
  }, 'Failed to update template');
  return useMutation({
    mutationFn: (args: { ctplid: number } & Partial<CMSTemplate>) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'templates'] }),
  });
};

export const useDeleteTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (ctplid: number) => {
    return request<any>(`${CMS}/templates/${ctplid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete template');
  return useMutation({
    mutationFn: (ctplid: number) => fn(ctplid),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'templates'] }),
  });
};

export const useApplyTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { ctplid: number; overrides?: object }) => {
    const raw = await request<any>(
      `${CMS}/templates/${args.ctplid}/apply`,
      'POST',
      args.overrides ?? {},
      TIMEOUT,
    );
    return unwrapPage(raw);
  }, 'Failed to apply template');
  return useMutation({
    mutationFn: (args: { ctplid: number; overrides?: object }) => fn(args),
    onSuccess: () => invalidatePage(qc),
  });
};

export const useCreateFieldDefMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (body: Partial<CMSFieldDefinition>) => {
    return request<any>(`${CMS}/fields`, 'POST', body, TIMEOUT);
  }, 'Failed to create field');
  return useMutation({
    mutationFn: (body: Partial<CMSFieldDefinition>) => fn(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'field-defs'] }),
  });
};

export const useUpdateFieldDefMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { cfdid: number } & Partial<CMSFieldDefinition>) => {
    const { cfdid, ...body } = args;
    return request<any>(`${CMS}/fields/${cfdid}`, 'PATCH', body, TIMEOUT);
  }, 'Failed to update field');
  return useMutation({
    mutationFn: (args: { cfdid: number } & Partial<CMSFieldDefinition>) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'field-defs'] }),
  });
};

export const useDeleteFieldDefMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (cfdid: number) => {
    return request<any>(`${CMS}/fields/${cfdid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete field');
  return useMutation({
    mutationFn: (cfdid: number) => fn(cfdid),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...cmsKeys.all, 'field-defs'] }),
  });
};

// Tags catalog mutations live in the focused tags module.
export {
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useMergeTagMutation,
  useActivateTagMutation,
  useBulkAssignTagEntitiesMutation,
} from '@/client/api/tags/tag.mutation';
